//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays, isObjectEmpty } from '@/utils';

//STP Libraries

//Assets and Miscellaneous

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
import type { SignedApplicationAssetResponse } from '@/types/Shared';

//Component Begin

export default function LegalStatus() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    setFocus,
    watch,
  } = useFormContext();

  const doesDocumentDefines = watch('step3.section1.doesDocumentDefines');

  const uploadFile = useMutation(
    (
      payloads: {
        signAssetPayload: ISignApplicationPublicAssetPayload[];
        files: File[];
        field: { key: string; description: string };
      }[]
    ) => Promise.allSettled(payloads.map((p: any) => upload(p))),
    {
      onSuccess: (
        res: any[],
        payloads: {
          signAssetPayload: ISignApplicationPublicAssetPayload[];
          files: File[];
          field: { key: string; description: string };
        }[]
      ) => {
        const updatedStep3 = { ...getValues('step3') };

        res.forEach((r, resIndex) => {
          if (r.status !== 'fulfilled') {
            setUploadError('Some files failed to upload. Please try those files again.');
            return;
          }
          const { sign } = r.value;
          const currentPayload = payloads[resIndex];
          let signRes: SignedApplicationAssetResponse[] = [];

          if (Array.isArray(sign)) {
            signRes = [...sign];
          } else {
            signRes = [{ ...sign }];
          }

          const relevantFiles = currentPayload.files.filter((file) =>
            currentPayload.signAssetPayload.some((payload) => payload.fileName === file.name)
          );

          updatedStep3[currentPayload.field.key] = {
            ...updatedStep3[currentPayload.field.key],
            selectedFiles: signRes.map((sig, index) => ({
              name: relevantFiles[index].name,
              path: sig.path,
              key: sig.key,
              type: relevantFiles[index].type,
              field: currentPayload.field.key,
              description: currentPayload.field.description,
            })),
          };
        });

        updateData({
          step3: updatedStep3,
        });
        setCurrentStep((prev) => prev + 1);
      },

      onError: (error: any) => {
        console.log(error);
        setUploadError('Failed to upload');
      },
    }
  );

  useEffect(() => {
    if (errors?.step3 && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.step3)[0];
      const firstErrorChildKey = Object.keys(get(errors, `step3.${firstErrorKey}`))[0];
      const firstErrorChildChildKey = Object.keys(get(errors, `step3.${firstErrorKey}.${firstErrorChildKey}`));
      firstErrorChildChildKey.forEach((d: any) => {
        setFocus(`step3.${firstErrorKey}.${firstErrorChildKey}.${d}`);
      });
    }
  }, [errors, setFocus]);

  const buildFileUploadPayload = () => {
    const formData = getValues('step3');
    const payloads: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
      field: { key: string; description: string };
    }[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];

      if (selectedFiles && selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.step3[f].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);
        const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();

    if (!isValid) {
      return;
    }
    const formData = getValues('step3');

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        step3: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Legal Status</h3>

      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm font-medium text-neutralColor-medium'>
            What document clearly defines your geographic service area?
          </h3>
          <p className='my-1 text-sm text-neutralColor-normal'>
            CHDOs do not need to represent a single neighborhood. A CHDO may include in its service area an entire community (that is, a
            city, town, village, county or multi-county area), but not the whole state. Non-profits serving special populations must also
            define the geographic boundaries of their service areas in order to qualify as CHDOs.
          </p>
          <HookRadioGroup
            name='step3.section1.doesDocumentDefines'
            options={[
              {
                label: 'Articles of Incorporation',
                value: 'articlesOfIncorporation',
              },
              { label: 'By-Laws', value: 'byLaws' },
              { label: 'Charter', value: 'charter' },
              { label: 'Map', value: 'map' },
            ]}
          />
        </div>
        {doesDocumentDefines === 'articlesOfIncorporation' && (
          <div className='col-span-3'>
            <HookInput name='step3.section1.articlesOfIncorporation.section' type='text' label='Section' />
            <HookInput name='step3.section1.articlesOfIncorporation.page' type='text' label='Page' />
            <HookInput name='step3.section1.articlesOfIncorporation.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {doesDocumentDefines === 'byLaws' && (
          <div className='col-span-3'>
            <HookInput name='step3.section1.byLaws.page' type='text' label='Page' />
            <HookInput name='step3.section1.byLaws.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {doesDocumentDefines === 'charter' && (
          <div className='col-span-3'>
            <HookInput name='step3.section1.charter.page' type='text' label='Page' />
            <HookInput name='step3.section1.charter.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step3.f1.selectedFiles'
            label='Notarized certificate that By-Laws, as provided are accurate'
            id='selectedFiles'
            multiple
          />
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='step3.f2.selectedFiles'
            label='Please provide documentation of your tax exemption ruling from the Internal Revenue Service (IRS) under Section 501(c)(3)   or (4) of the Internal Revenue Code of 1986'
            id='selectedFiles'
            multiple
          />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
      </div>

      <div className='flex gap-4 justify-end mt-5'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
